import api from '../api';

// Types for seat assignments
export interface SeatAssignment {
    _id: string;
    eventId: string;
    venueId: string;
    ticketId?: string | null;
    seatUuid: string;
    orderHashUniqueId?: string;
    orderTicketQrCodeId?: string;
    status: string;
    holdNote?: string;
    assignedBy: string;
    createdAt: Date;
    updatedAt: Date;
    orderInfo?: {
        customerName?: string;
        customerEmail?: string;
        customerPhone?: string;
    };
}

export interface CreateSeatAssignmentRequest {
    eventId: string;
    venueId: string;
    ticketId: string;
    seatUuid: string;
    orderHashUniqueId?: string;
    orderTicketQrCodeId?: string;
    status?: string;
}

export interface BulkSeatAssignmentRequest {
    eventId: string;
    assignments: CreateSeatAssignmentRequest[];
}

export interface SeatAssignmentListResponse {
    data: SeatAssignment[];
    total: number;
}

// API functions
export async function fetchSeatAssignmentsByEvent(
    eventId: string
): Promise<SeatAssignmentListResponse> {
    const res = await api.get(`/eventopia/seat-assignments/event/${eventId}`);
    return res.data;
}

export async function fetchSeatAssignmentsByEventWithOrderInfo(
    eventId: string
): Promise<SeatAssignmentListResponse> {
    const res = await api.get(`/eventopia/seat-assignments/event/${eventId}/with-order-info`);
    return res.data;
}

export async function bulkCreateSeatAssignments(
    bulkData: BulkSeatAssignmentRequest
): Promise<{ success: boolean; data: SeatAssignment[]; message: string }> {
    const res = await api.post('/eventopia/seat-assignments/bulk', bulkData);
    return res.data;
}

/**
 * Uploads seat assignment JSON file to backend for processing
 * @param formData - FormData containing the JSON file and metadata
 * @returns Promise with upload result
 */
export async function uploadSeatAssignmentJSON(
    formData: FormData
): Promise<{ success: boolean; data: SeatAssignment[]; message: string; processedCount: number }> {
    const res = await api.post('/eventopia/seat-assignments/upload-json', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return res.data;
}


export type SeatBatchResult = { success: boolean; updated: number; skipped: string[] };

export async function assignSeats(eventId: string, seatUuids: string[], ticketId: string): Promise<SeatBatchResult> {
    const res = await api.post(`/eventopia/seat-assignments/event/${eventId}/assign`, { seatUuids, ticketId });
    return res.data;
}

export async function holdSeats(eventId: string, seatUuids: string[], note?: string): Promise<SeatBatchResult> {
    const res = await api.post(`/eventopia/seat-assignments/event/${eventId}/hold`, { seatUuids, ...(note ? { note } : {}) });
    return res.data;
}

export async function releaseSeats(eventId: string, seatUuids: string[]): Promise<SeatBatchResult> {
    const res = await api.post(`/eventopia/seat-assignments/event/${eventId}/release`, { seatUuids });
    return res.data;
}

export async function clearSeats(eventId: string, opts: { seatUuids?: string[]; ticketId?: string } = {}): Promise<SeatBatchResult> {
    const res = await api.post(`/eventopia/seat-assignments/event/${eventId}/clear`, opts);
    return res.data;
}
